    import React, { useState, useEffect } from 'react';
    import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
    import { getFirestore, doc, getDoc } from 'firebase/firestore';
    import { app } from './firebaseConfig';
    import './App.css';
    import AuthPage from './AuthPage';
    import PaymentPage from './PaymentPage';

    const auth = getAuth(app);
    const db = getFirestore(app);

    function App() {
      const [user, setUser] = useState(null);
      const [loadingUser, setLoadingUser] = useState(true);
      const [userProfile, setUserProfile] = useState(null);
      const [currentPage, setCurrentPage] = useState('auth'); // New state for explicit page management

      useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          console.log("App.js: onAuthStateChanged - currentUser:", currentUser ? currentUser.uid : "null");
          setUser(currentUser);
          setLoadingUser(false);

          if (currentUser) {
            try {
              const docRef = doc(db, 'users', currentUser.uid);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                console.log("App.js: User profile found for", currentUser.uid);
                const profileData = docSnap.data();
                setUserProfile(profileData);
                // After successful auth and profile fetch, determine page
                if (profileData?.paymentStatus === 'paid' && profileData?.membershipExpiry && new Date() < profileData.membershipExpiry.toDate()) {
                  setCurrentPage('main');
                } else {
                  setCurrentPage('payment');
                }
              } else {
                console.warn("App.js: User profile NOT found in Firestore for:", currentUser.uid);
                setUserProfile(null);
                // If user authenticated but no profile, send to payment page (or profile creation)
                setCurrentPage('payment');
              }
            } catch (err) {
              console.error("App.js: Error fetching user profile:", err);
              setUserProfile(null);
              // If error fetching profile, send to payment page
              setCurrentPage('payment');
            }
          } else {
            console.log("App.js: No user authenticated. Setting current page to 'auth'.");
            setUserProfile(null);
            setCurrentPage('auth'); // Always show auth page if no user
          }
        });
        return () => unsubscribe();
      }, []);

      const handleAuthSuccess = (authUser) => {
        console.log("App.js: handleAuthSuccess called for user:", authUser ? authUser.uid : "null");
        setUser(authUser); // This will trigger the useEffect to fetch profile and set page
        // No explicit setCurrentPage here, let useEffect handle it after profile fetch
      };

      const handlePaymentSuccess = () => {
        console.log("App.js: handlePaymentSuccess called. Moving to main content.");
        setUserProfile(prev => ({
          ...prev,
          paymentStatus: 'paid',
          membershipExpiry: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        }));
        setCurrentPage('main'); // Explicitly move to main page
      };

      const handleSignOut = async () => {
        try {
          await signOut(auth);
          console.log("App.js: User signed out from App.js.");
          // onAuthStateChanged will set user to null and trigger setCurrentPage('auth')
        } catch (error) {
          console.error("App.js: Error signing out:", error);
        }
      };

      if (loadingUser) {
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-100 font-sans">
            <p className="text-xl text-gray-700">Loading user session...</p>
          </div>
        );
      }

      switch (currentPage) {
        case 'auth':
          return <AuthPage onAuthSuccess={handleAuthSuccess} />;
        case 'payment':
          // Ensure user is not null when passing to PaymentPage, although it should be handled by logic
          return <PaymentPage user={user} onPaymentSuccess={handlePaymentSuccess} onSignOut={handleSignOut} />;
        case 'main':
          return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
              <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center rounded-lg">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Main Application Content</h2>
                <p className="text-gray-600 mb-6">Welcome! You are logged in and your membership is active.</p>
                <p className="text-gray-600 mb-6">This is where your actual website content would go.</p>
                <button
                  onClick={handleSignOut}
                  className="w-full py-2 px-4 bg-red-600 text-white font-semibold rounded-md shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
                >
                  Sign Out
                </button>
              </div>
            </div>
          );
        default:
          return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 font-sans">
              <p className="text-xl text-red-700">Error: Unknown application state.</p>
              <button onClick={handleSignOut}>Sign Out</button> {/* Provide a way out */}
            </div>
          );
      }
    }

    export default App;
    